import {
  flatten,
  has,
  pipe,
  equals,
  propEq,
  ifElse,
  always,
  pathSatisfies,
  gt,
  reject,
  isNil,
  test,
  propSatisfies,
  length,
  apply,
  when
} from 'ramda'

/**
 * Returns the postfix
 * `y` for 1
 * `ies` for values greater than 1
 */
const multiple = ifElse(equals(1), always('y'), always('ies'))

/**
 * Checks that there's less entries than the specified count
 * @param {number} entryCount - the amount of entries allowed
 * @param {Object[]} arr
 * @returns {string|undefined} the validation error
 */
const validateEntryCount = entryCount => arr => {
  const excedingEntries = arr.length - entryCount

  if (arr.length > entryCount) {
    return `${excedingEntries} entr${multiple(excedingEntries)} too many`
  }
}

/**
 * Counts the number of elements in an array where the condition is not met
 * @param {Object[]} arr
 * @param {string} propName
 * @returns {number} the number of items without the prop
 */
const count = f =>
  pipe(
    reject(f),
    length
  )

/**
 * Validates a given prop based on whether it is required or not
 * if it is required, the prop _must_ be in all elements
 * if it is not required, it must be in either all of them or none of them
 * @param {string} propName - the prop that will be validated
 * @param {boolean} required - states whether the prop is required(true) or optional(false)
 * @param {Object[]} arr
 * @returns {string|undefined} the validation error
 */
const validateIncludedProps = (propName, required = true) => arr => {
  const total = arr.length

  const hasProperty = has(propName)
  const missingCount = count(hasProperty)(arr)

  const optionalMissing = missingCount > 0 && missingCount < total

  if (required ? missingCount : optionalMissing) {
    return `Missing ${propName}: ${missingCount} entr${multiple(missingCount)}`
  }
}

const validateProps = props => items =>
  props.map(apply(validateIncludedProps)).map(f => f(items))

/**
 * Validates that a value doesn't excede the specified length
 * @param {string} propName - the prop that will be validated
 * @param {number} maxLength - the max length of the prop
 * @param {Object[]} arr
 * @returns {string|undefined} the validation error
 */
const validateValueLengths = (propName, maxLength) => arr => {
  const gtThanMaxLength = when(
    has(propName),
    pathSatisfies(gt(maxLength), [propName, 'length'])
  )
  const tooLongEntryCount = count(gtThanMaxLength)(arr)

  if (tooLongEntryCount) {
    return `Too long ${propName} (character limit is ${maxLength}): ${tooLongEntryCount} entr${multiple(
      tooLongEntryCount
    )}`
  }
}

/**
 * Validates that the given value is equal to the value of the propName specified for each item in the list
 * @param {string} propName - the prop that will be validated
 * @param {number} value - value that the property should be
 * @param {Object[]} arr
 * @returns {string|undefined} the validation error
 */

const validatePropertyValues = (propName, expected) => arr => {
  const equalExpected = when(has(propName), propEq(propName, expected))
  const incorrectValueCount = count(equalExpected)(arr)

  if (incorrectValueCount > 0) {
    return `Incorrect ${propName}: ${incorrectValueCount} entr${multiple(
      incorrectValueCount
    )}`
  }
}

/**
 * Validates that the URLs added for each entry are valid
 * @param {string} propName - the prop that will be validated
 * @param {Object[]} arr
 * @returns {string|undefined} the validation error
 */

const validateUrls = propName => arr => {
  const url = /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/ // eslint-disable-line

  const testUrl = when(has(propName), propSatisfies(test(url), propName))
  const invalidUrls = count(testUrl)(arr)

  if (invalidUrls > 0) {
    return `Invalid URL: ${invalidUrls} entr${multiple(invalidUrls)}`
  }
}

/**
 * Validates that the input parses to valid JSON before it runs through the rest of the validations specified and returning the relevant errors
 * @param {Function} validatorFunctions - validation functions
 * @param {string} value
 * @returns {string[]} an array of validation errors
 */

const validate = (...validatorFunctions) => value => {
  if (value) {
    let items

    try {
      items = JSON.parse(value)
    } catch (e) {
      return { json: true }
    }

    const callValidators = list => validatorFunctions.map(f => f(list))

    const getErrors = pipe(
      callValidators,
      flatten,
      reject(isNil)
    )

    const errors = getErrors(items)

    if (errors.length) return errors
  }
}

export const validateQuickReplies = validate(
  validateEntryCount(20),
  validateProps([['label'], ['metadata'], ['description', false]]),
  validateValueLengths('label', 36),
  validateValueLengths('description', 72)
)

export const validateCallToActions = validate(
  validateEntryCount(3),
  validateProps([['label'], ['type'], ['url']]),
  validatePropertyValues('type', 'web_url'),
  validateUrls('url'),
  validateValueLengths('label', 36)
)



// WEBPACK FOOTER //
// ./src/pages/widgets/form/tweet-input/dm-attachments/validation.js